// Beacon (M20.1). PURE (ENG-1): the pyramid-tier detection a beacon runs over the
// mineral blocks beneath it, the effect menu each tier unlocks, and the range and
// duration it projects. A beacon needs a complete pyramid of 1..MAX_TIER
// mineral-block layers directly below it (and clear sky above — the host checks
// that) to power an effect the player picks from the tier's menu.
import { BEACON } from '../config';

/** Blocks a beacon pyramid may be built from (MC 1.21). */
export const BEACON_BASE_BLOCKS: readonly string[] = [
  'iron_block', 'gold_block', 'emerald_block', 'diamond_block', 'netherite_block',
];
export function isBeaconBase(block: string | null): boolean {
  return block !== null && BEACON_BASE_BLOCKS.includes(block);
}

/**
 * The pyramid tier (0..MAX_TIER) beneath a beacon. Layer L (1-based) is the
 * (2L+1)×(2L+1) square centred under the beacon at depth L; the tier is the count
 * of complete layers from the top (layer 1) down with no gap. `baseAt(dx, depth,
 * dz)` reads the block `depth` cells below the beacon at horizontal offset (dx,dz).
 */
export function beaconTier(baseAt: (dx: number, depth: number, dz: number) => string | null): number {
  let tier = 0;
  for (let layer = 1; layer <= BEACON.MAX_TIER; layer++) {
    let complete = true;
    for (let dx = -layer; dx <= layer && complete; dx++) {
      for (let dz = -layer; dz <= layer; dz++) {
        if (!isBeaconBase(baseAt(dx, layer, dz))) { complete = false; break; }
      }
    }
    if (!complete) break;
    tier = layer;
  }
  return tier;
}

/** Primary effects a beacon of `tier` can project (cumulative up the tiers). */
export function beaconPrimaryChoices(tier: number): readonly string[] {
  const t = Math.max(0, Math.min(BEACON.MAX_TIER, Math.floor(tier)));
  const out: string[] = [];
  if (t >= 1) out.push('speed', 'haste');
  if (t >= 2) out.push('resistance', 'jump_boost');
  if (t >= 3) out.push('strength');
  return out;
}

/** A max-tier beacon unlocks a second effect: either Regeneration, or the primary
 *  boosted to level II (secondary === primary). */
export function beaconSecondaryUnlocked(tier: number): boolean {
  return Math.floor(tier) >= BEACON.MAX_TIER;
}

export interface BeaconEffect { readonly id: string; readonly amplifier: number; }

/**
 * The effects a powered beacon applies for a (tier, primary, secondary) selection.
 * An invalid or unselected primary powers nothing. At max tier, secondary ===
 * primary boosts it to level II (amplifier 1); secondary === 'regeneration' adds a
 * Regeneration I alongside the primary. Below max tier the secondary is ignored.
 */
export function resolveBeaconEffects(tier: number, primary: string | null, secondary: string | null): BeaconEffect[] {
  const t = Math.floor(tier);
  if (t <= 0 || !primary || !beaconPrimaryChoices(t).includes(primary)) return [];
  if (beaconSecondaryUnlocked(t) && secondary) {
    if (secondary === primary) return [{ id: primary, amplifier: 1 }];
    if (secondary === 'regeneration') return [{ id: primary, amplifier: 0 }, { id: 'regeneration', amplifier: 0 }];
  }
  return [{ id: primary, amplifier: 0 }];
}

/** Horizontal block radius a beacon of `tier` reaches (MC: tier·10 + 10). */
export function beaconRange(tier: number): number {
  return Math.floor(tier) * BEACON.RANGE_PER_TIER + BEACON.RANGE_BASE;
}

/** Effect duration (ticks) reapplied every BEACON.APPLY_INTERVAL (MC: (9 + tier·2)·20). */
export function beaconDuration(tier: number): number {
  return (BEACON.DURATION_BASE_SECONDS + Math.floor(tier) * BEACON.DURATION_PER_TIER_SECONDS) * 20;
}

/** Whether a player at (dx,dy,dz) relative to the beacon is inside its box of
 *  influence: within the horizontal reach and a generous vertical band (MC's box
 *  spans from a few blocks below the base to the world ceiling). */
export function beaconInRange(tier: number, dx: number, dy: number, dz: number): boolean {
  if (Math.floor(tier) <= 0) return false;
  const r = beaconRange(tier);
  return Math.abs(dx) <= r && Math.abs(dz) <= r && dy <= BEACON.VERTICAL_UP && dy >= -BEACON.VERTICAL_DOWN;
}
